﻿import {runSyncAsync, translation} from "@2d-front-monorepo/core";
import {showErrorDialog} from "@2d-front-monorepo/design-library";
import {CreditCard, CreditCardProvider, formatDate, getCreditCardProviderName} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {getHttpClient} from "../hooks/getHttpClient";
import {ReloadEntities, reloadPermits} from "../hooks/reloadPermits";
import {CreditCardAssignment} from "../hooks/useCreditCardAssignment";
import Loader from "./Loader";
import MaterialIcon from "./MaterialIcon";
import {JSX, useCallback, useState} from "react";

type AddCreditCardProps = {
	startRegister: () => Promise<void>;
}

function Loading(): JSX.Element {
	return (
		<div className={"card credit-card"}>
			<div className={"credit-card-add"}>
				<Loader/>
				<span>{translation(PermitTranslations.change_in_progress)}</span>
			</div>
		</div>
	);
}

function AddCreditCard(props: AddCreditCardProps): JSX.Element {
	const {startRegister} = props;

	return (
		<div className={"card credit-card"}>
			<div className={"credit-card-add"}>
				<button onClick={startRegister} type="button">
					<MaterialIcon icon="add"/>
					<span>{translation(PermitTranslations.payments_page__add_new_card)}</span>
				</button>
			</div>
		</div>
	);
}

type SelectedCreditCardProps = {
	allowUpdate?: boolean;
	allowRemove?: boolean;
	creditCard: CreditCard;
	startRegister: () => Promise<void>;
	removeCard: () => Promise<void>;
}

function SelectedCreditCard(props: SelectedCreditCardProps): JSX.Element {
	const {creditCard, startRegister, removeCard, allowUpdate = false, allowRemove = false} = props;

	return (
		<div className={"card layout vertical credit-card"}>
			<div>
				<div className="credit-card--icon"><MaterialIcon icon="credit_card"/></div>
				<div className="credit-card--text">
					<span>{creditCard.number}</span>
					{creditCard.expires && <small>{translation(PermitTranslations.credit_card__expiration, {value: formatDate(creditCard.expires, "smonth_year")})}</small>}
					<small>{getCreditCardProviderName(creditCard.provider)}</small>
				</div>
			</div>
			{(allowUpdate || allowRemove) && <>
                <hr/>
                <div>
					{allowRemove && <button onClick={removeCard} type="button">{translation(PermitTranslations.payments_page__remove_card)}</button>}
					{allowUpdate && <button onClick={startRegister} type="button">{translation(PermitTranslations.payments_page__change_card)}</button>}
                </div>
            </>}
		</div>
	);
}

export type AddUpdateCreditCardProps = {
	creditCardAssignment: CreditCardAssignment;
	allowUpdate?: boolean;
	allowRemove?: boolean;
	onAdd?: () => void | Promise<void>;
	onRemove?: () => void | Promise<void>;
}

export function AddUpdateCreditCard(props: AddUpdateCreditCardProps): JSX.Element {
	const {onAdd, onRemove, allowUpdate, allowRemove, creditCardAssignment} = props;
	const [loading, setLoading] = useState(() => false);
	const {creditCard, registerCreditCard} = creditCardAssignment;

	const startRegister = useCallback(async function (): Promise<void> {
		try {
			setLoading(true);
			await registerCreditCard();
			await reloadPermits(ReloadEntities.Holder);
			if (onAdd) {
				await runSyncAsync(onAdd);
			}

		} catch (error) {
			showErrorDialog(error);
		} finally {
			setLoading(false);
		}

	}, [registerCreditCard, onAdd]);

	const remove = useCallback(async function (): Promise<void> {
		if (!confirm(translation(PermitTranslations.payments_page__remove_card__remove, {number: creditCard.number}))) {
			return;
		}

		try {
			setLoading(true);
			await getHttpClient().payments.remove(creditCard.provider);
			await reloadPermits(ReloadEntities.Holder);

			if (onRemove) {
				await runSyncAsync(onRemove);
			}

		} catch (error) {
			showErrorDialog(error);
		} finally {
			setLoading(false);
		}

	}, [creditCard.provider, onRemove]);

	return (
		<>
			{loading && <Loading/>}
			{loading ? undefined : creditCard.provider == CreditCardProvider.Undefined
				? <AddCreditCard startRegister={startRegister}/>
				: <SelectedCreditCard creditCard={creditCard} startRegister={startRegister} removeCard={remove} allowUpdate={allowUpdate} allowRemove={allowRemove}/>
			}
		</>
	);
}
